import { useMemo } from "react";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import L from "leaflet";
import type { Shop } from "@shared/schema";
import { getIconForShop, inferShopTypeFromName } from "@/lib/mapIcons";
import { OSM_TILE_ATTRIBUTION, OSM_TILE_URL } from "@/lib/osmMaps";
import { useI18n } from "@/lib/i18n";
import { MapInteractionShield } from "./MapInteractionShield";
import { useRoutingConfig } from "@/hooks/use-routing-config";


interface ShopMapProps {
  shop: Shop;
  height?: string;
  className?: string;
}

function isValidLocation(shop: Shop): boolean {
  return (
    shop.lat != null &&
    shop.lng != null &&
    typeof shop.lat === "number" &&
    typeof shop.lng === "number" &&
    !Number.isNaN(shop.lat) &&
    !Number.isNaN(shop.lng)
  );
}

function makeShopIcon(shop: Shop): L.Icon {
  const shopType =
    shop.has_charity !== undefined
      ? shop
      : { ...shop, ...inferShopTypeFromName((typeof shop.name === "string" ? shop.name : (shop.name as any)?.value) || "") };
  const icon = getIconForShop(shopType);
  return L.icon({
    iconUrl: icon.url,
    iconSize: [icon.scaledSize?.width || 96, icon.scaledSize?.height || 96],
    iconAnchor: [icon.anchor?.x || 48, icon.anchor?.y || 72],
  });
}

export function ShopMapOsm({ shop, height = "200px", className = "" }: ShopMapProps) {
  const { t } = useI18n();
  const center = useMemo<[number, number]>(() => [shop.lat as number, shop.lng as number], [shop.lat, shop.lng]);
  const icon = useMemo(() => makeShopIcon(shop), [shop]);
  if (!isValidLocation(shop)) {
    return (
      <div className={`bg-muted rounded-lg flex items-center justify-center ${className}`} style={{ height }}>
        <p className="text-muted-foreground text-sm">{t("map.locationUnavailable")}</p>
      </div>
    );
  }

  const tileUrl = OSM_TILE_URL;

  return (
    <div className="relative w-full z-0" style={{ height }}>
      <MapInteractionShield>
        <MapContainer center={center} zoom={17} className={`rounded-lg ${className}`} style={{ width: "100%", height: "100%" }}>
          <TileLayer url={tileUrl} attribution={OSM_TILE_ATTRIBUTION} />
          <Marker position={center} icon={icon} />
        </MapContainer>
      </MapInteractionShield>
    </div>
  );
}

